// Secao de Estoques (Fase 1) — super_admin. Espelho dos depositos Sisplan + saude
// (frescura da ORIGEM) + total + reconciliacao do atacado + detector na fonte.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { RefreshCw, RadioTower } from "lucide-react";
import { api, apiErrorMessage } from "../../lib/api";
import { PageHeader } from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import { EmptyState } from "../../components/ui/empty-state";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { DataTable, type DataTableColumn } from "../../components/ui/DataTable";
import { formatDate } from "../../lib/utils";
import { useAuth } from "../../contexts/AuthContext";

interface StockLocation {
  sisplan_deposito: string;
  name: string;
  slug: string;
  pool: string | null;
  active: boolean;
  auto_detected: boolean;
  variacoes: number;
  total_unidades: number;
  mirror_synced_at: string | null;
  origin_updated_at: string | null;
  origin_age_sec: number | null;
  /** Última importação bem-sucedida do Sisplan (todos os depósitos); API antiga não manda. */
  origin_checked_at?: string | null;
  origin_check_age_sec?: number | null;
  health: "green" | "yellow" | "red" | "unknown";
}

const HEALTH_BADGE: Record<StockLocation["health"], { variant: "success" | "warning" | "destructive" | "outline"; label: string }> = {
  green: { variant: "success", label: "fresco" },
  yellow: { variant: "warning", label: "atrasado" },
  red: { variant: "destructive", label: "defasado" },
  unknown: { variant: "outline", label: "sem dado" },
};

function ageLabel(sec: number | null): string {
  if (sec == null) return "—";
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}min`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  return `${Math.floor(sec / 86400)}d`;
}

export function AdminStockPage(): JSX.Element {
  return (
    <div>
      <PageHeader
        title="Estoques"
        description="Espelho dos depósitos do Sisplan (somente leitura). Saúde pela frescura da origem, reconciliação vs publicado por canal."
      />
      <Tabs defaultValue="depositos">
        <TabsList>
          <TabsTrigger value="depositos">Depósitos</TabsTrigger>
          <TabsTrigger value="reconciliacao">Reconciliação</TabsTrigger>
        </TabsList>
        <TabsContent value="depositos"><DepositosTab /></TabsContent>
        <TabsContent value="reconciliacao"><ReconciliacaoTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function DepositosTab(): JSX.Element {
  const qc = useQueryClient();
  // Rota exige so stock.read (App.tsx); "Carregar agora"/"Detectar orfaos"
  // sao mutations e exigem stock.manage — sem isto o botao aparecia
  // clicavel pra quem so tem leitura e a API negava com 403 (BUG-01 do QA).
  const { can } = useAuth();
  const canManage = can("stock.manage");
  const { data, isLoading, isError, error } = useQuery<{ locations: StockLocation[] }>({
    queryKey: ["stock-locations"],
    queryFn: async () => (await api.get<{ locations: StockLocation[] }>("/api/admin/stock/locations")).data,
  });

  const refresh = useMutation({
    mutationFn: async () =>
      (await api.post<{ rows_mirrored: number; locations: number; mirrored_at: string }>("/api/admin/stock/refresh")).data,
    onSuccess: (r) => {
      toast.success(`Carregado: ${r.rows_mirrored} linhas, ${r.locations} depósitos (${formatDate(r.mirrored_at)})`);
      qc.invalidateQueries({ queryKey: ["stock-locations"] });
    },
    onError: (err) => toast.error(apiErrorMessage(err)),
  });

  const detect = useMutation({
    mutationFn: async () =>
      (await api.post<{ all_clear: boolean; orphans: { codigo: string; nome: string | null }[]; notified: number }>(
        "/api/admin/stock/detect-orphans",
      )).data,
    onSuccess: (r) => {
      if (r.all_clear) toast.success("Detector: nenhum depósito órfão no Sisplan");
      else toast.warning(`Detector: ${r.orphans.length} depósito(s) não importado(s): ${r.orphans.map((o) => o.codigo).join(", ")}`);
    },
    onError: (err) => toast.error(apiErrorMessage(err)),
  });

  if (isLoading) return <Skeleton className="h-64 w-full mt-4" />;
  if (isError) return <EmptyState title="Erro" description={apiErrorMessage(error)} />;
  const locations = data?.locations ?? [];
  const totalUnidades = locations.reduce((s, l) => s + l.total_unidades, 0);
  const totalVariacoes = locations.reduce((s, l) => s + l.variacoes, 0);

  return (
    <div className="stack" style={{ marginTop: "1rem" }}>
      <div className="spread">
        <div style={{ fontSize: ".8125rem", fontWeight: 700, color: "var(--color-ink-2)" }}>
          Total: <b className="mono" style={{ color: "var(--color-ink)" }}>{totalUnidades.toLocaleString("pt-BR")}</b> unidades
          em <b className="mono" style={{ color: "var(--color-ink)" }}>{totalVariacoes.toLocaleString("pt-BR")}</b> variações
          ({locations.length} depósitos)
        </div>
        {canManage ? (
          <div className="cluster">
            <Button variant="secondary" onClick={() => detect.mutate()} loading={detect.isPending}>
              <RadioTower className="icon" aria-hidden /> Detectar órfãos
            </Button>
            <Button onClick={() => refresh.mutate()} loading={refresh.isPending}>
              <RefreshCw className="icon" aria-hidden /> Carregar agora
            </Button>
          </div>
        ) : null}
      </div>

      {locations.length === 0 ? (
        <EmptyState
          title="Nenhum depósito espelhado"
          description={canManage ? 'Clique em "Carregar agora" para trazer os depósitos do Sisplan.' : "Seu perfil permite ver os depósitos, mas não carregá-los."}
        />
      ) : (
        <div className="cards" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(15rem, 1fr))" }}>
          {locations.map((l) => {
            const h = HEALTH_BADGE[l.health];
            return (
              <div
                key={l.sisplan_deposito}
                className="card stack"
                style={{ borderTop: ".1875rem solid var(--color-sun)" }}
              >
                <div className="spread" style={{ alignItems: "flex-start" }}>
                  <div className="grow">
                    <div style={{ fontSize: ".9375rem", fontWeight: 900 }}>{l.name}</div>
                    <div className="mono truncate" style={{ fontSize: ".6875rem", color: "var(--color-ink-3)" }}>
                      {l.sisplan_deposito} · {l.slug}
                      {l.auto_detected ? (
                        <Badge variant="outline" style={{ marginLeft: ".25rem" }}>auto</Badge>
                      ) : null}
                    </div>
                  </div>
                  <Badge variant={h.variant}>{h.label}</Badge>
                </div>
                <div className="grid-12" style={{ gap: ".5rem" }}>
                  <div className="gc-6">
                    <div style={{ fontSize: ".6875rem", fontWeight: 800, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--color-ink-3)" }}>
                      Variações
                    </div>
                    <div className="mono" style={{ fontSize: "1.125rem", fontWeight: 900 }}>
                      {l.variacoes.toLocaleString("pt-BR")}
                    </div>
                  </div>
                  <div className="gc-6">
                    <div style={{ fontSize: ".6875rem", fontWeight: 800, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--color-ink-3)" }}>
                      Unidades
                    </div>
                    <div className="mono" style={{ fontSize: "1.125rem", fontWeight: 900 }}>
                      {l.total_unidades.toLocaleString("pt-BR")}
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: ".6875rem", fontWeight: 700, color: "var(--color-ink-3)" }}>
                  Pool: {l.pool ?? "—"} · Conferido (importação do Sisplan, todos os depósitos):{" "}
                  {l.origin_checked_at ? `${formatDate(l.origin_checked_at)} (há ${ageLabel(l.origin_check_age_sec ?? null)})` : "—"}
                  {" "}· Última mudança:{" "}
                  {l.origin_updated_at ? `${formatDate(l.origin_updated_at)} (há ${ageLabel(l.origin_age_sec)})` : "—"}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface ReconResponse {
  summary: {
    front: string;
    channel_keys: string[];
    skus: number;
    ok: number;
    mismatch: number;
    only_origin: number;
    only_published: number;
    total_origin: number;
    total_published: number;
  };
  discrepancies: { sku: string; origin_qty: number | null; published_qty: number | null; delta: number; status: string }[];
}

function ReconciliacaoTab(): JSX.Element {
  const [front] = useState("atacado");
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery<ReconResponse>({
    queryKey: ["stock-reconciliation", front],
    queryFn: async () =>
      (await api.get<ReconResponse>(`/api/admin/stock/reconciliation?front=${front}`)).data,
  });

  if (isLoading) return <Skeleton className="h-64 w-full mt-4" />;
  if (isError) return <EmptyState title="Erro" description={apiErrorMessage(error)} />;
  const s = data!.summary;

  const columns: DataTableColumn<ReconResponse["discrepancies"][number]>[] = [
    { key: "sku", label: "SKU", width: "minmax(6rem,2fr)", mono: true, render: (d) => d.sku },
    { key: "origin", label: "Origem", width: "minmax(4rem,1fr)", mono: true, render: (d) => d.origin_qty ?? "—" },
    { key: "published", label: "Publicado", width: "minmax(4rem,1fr)", mono: true, render: (d) => d.published_qty ?? "—" },
    { key: "delta", label: "Delta", width: "minmax(4rem,1fr)", mono: true, render: (d) => d.delta },
    {
      key: "status",
      label: "Status",
      width: "minmax(6rem,1.5fr)",
      render: (d) => <Badge variant={d.status === "mismatch" ? "warning" : "outline"}>{d.status}</Badge>,
    },
  ];

  return (
    <div className="stack" style={{ marginTop: "1rem" }}>
      <div className="spread">
        <div style={{ fontSize: ".8125rem", fontWeight: 700, color: "var(--color-ink-2)" }}>
          Frente <span className="mono" style={{ color: "var(--color-ink)" }}>{s.front}</span> · canais{" "}
          <span className="mono" style={{ color: "var(--color-ink)" }}>{s.channel_keys.join(", ")}</span>
        </div>
        <Button variant="secondary" onClick={() => void refetch()} loading={isFetching}>
          <RefreshCw className="icon" aria-hidden /> Recalcular
        </Button>
      </div>

      <div className="cards" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(10rem, 1fr))" }}>
        <StatCard label="Origem (total)" value={s.total_origin.toLocaleString("pt-BR")} />
        <StatCard label="Publicado (total)" value={s.total_published.toLocaleString("pt-BR")} />
        <StatCard label="SKUs divergentes" value={String(s.mismatch)} tone="warning" />
        <StatCard label="Só origem / só publicado" value={`${s.only_origin} / ${s.only_published}`} />
      </div>

      <DataTable
        columns={columns}
        rows={data!.discrepancies}
        rowKey={(d) => d.sku}
        emptyState={<EmptyState title="Sem divergências" />}
        renderCard={(d) => (
          <div className="row-card">
            <div className="row-card__body">
              <span className="mono" style={{ fontSize: ".75rem" }}>{d.sku}</span>
              <span className="cluster" style={{ fontSize: ".75rem", color: "var(--color-ink-2)" }}>
                Origem {d.origin_qty ?? "—"} · Publicado {d.published_qty ?? "—"} · Delta {d.delta}
              </span>
              <Badge variant={d.status === "mismatch" ? "warning" : "outline"}>{d.status}</Badge>
            </div>
          </div>
        )}
      />
      <p style={{ fontSize: ".75rem", color: "var(--color-ink-2)" }}>
        Diferença origem vs publicado é esperada (o n8n aplica regras de alocação por canal). A virada para a vitrine
        publicar estoque é Fase 2. Comparação por SKU, variação a variação fica para a Fase 2.
      </p>
    </div>
  );
}

function StatCard({ label, value, tone }: { label: string; value: string; tone?: "warning" }): JSX.Element {
  return (
    <div className="card">
      <div style={{ fontSize: ".6875rem", fontWeight: 800, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--color-ink-3)" }}>
        {label}
      </div>
      <div className="mono" style={{ fontSize: "1.25rem", fontWeight: 900, color: tone === "warning" ? "var(--color-warn)" : "var(--color-ink)" }}>
        {value}
      </div>
    </div>
  );
}
